<script>
import ProfileLayout from "@/Layouts/ProfileLayout.vue";

export default {
    layout: ProfileLayout,
};
</script>

<script setup>
import { computed, onMounted } from "vue";
import { Head, router, usePage } from "@inertiajs/vue3";
import { useGracePoll } from "@/composables/useGracePoll.js";
import { useModalogStore } from "@/stores/modalog.js";
import { useWishlistStore } from "@/stores/wishlist.js";
import AddGiftCard from "@/Components/Profile/AddGiftCard.vue";
import ConfirmedGiftsToggle from "@/Components/Profile/ConfirmedGiftsToggle.vue";
import GiftCard from "@/Components/Profile/GiftCard.vue";
import ListsPagination from "@/Components/Profile/ListsPagination.vue";
import OnboardingCard from "@/Components/Profile/OnboardingCard.vue";
import SortDropdown from "@/Components/Profile/SortDropdown.vue";
import WishListDetailHero from "@/Components/Profile/WishListDetailHero.vue";
import WishListMasthead from "@/Components/Profile/WishListMasthead.vue";

const modalogStore = useModalogStore();
const wishlistStore = useWishlistStore();

const page = usePage();

const owner = computed(() => page.props.owner?.data);
const giftlist = computed(() => page.props.giftlist?.data);
const items = computed(() => page.props.items?.data ?? []);
const meta = computed(() => page.props.items?.meta);

const isConfirmedView = computed(() => route().current() === "list.confirmed");
const isExpertList = computed(() => giftlist.value?.type === "expert");
// Gift Heroes' expert lists get the editorial header (GH-1299).
const isGiftHeroExpertList = computed(
    () => isExpertList.value && !!owner.value?.is_gift_hero,
);

const totalGifts = computed(() => meta.value?.total ?? items.value.length);

const giftsHeading = computed(
    () =>
        `${totalGifts.value} ${totalGifts.value === 1 ? "Gift" : "Gifts"} In This List`,
);

// Expert lists have no confirmations, and an empty list has nothing to
// reveal yet (BH-37).
const showConfirmedButton = computed(
    () => !isExpertList.value && totalGifts.value > 0,
);

// --- Meta tags (parity with the legacy WishListLayout) ---
const ownerName = computed(() => owner.value?.presentation_name);
const openGraph = computed(() => giftlist.value?.open_graph);
const pageDescription = computed(
    () =>
        `${ownerName.value}'s "${giftlist.value?.name}" Wish List on Gift Hero`,
);
const noIndex = computed(
    () =>
        giftlist.value?.privacy === "private" ||
        (!owner.value?.is_gift_hero && !owner.value?.is_organization),
);

// --- Server-side sorting (Inertia partial reloads) ---
const SORT_OPTIONS = [
    {
        value: "recent",
        label: "Most Recent",
        orderBy: "sequence",
        sortDirection: "desc",
    },
    {
        value: "oldest",
        label: "Oldest First",
        orderBy: "created_at",
        sortDirection: "asc",
    },
    {
        value: "price-low",
        label: "Price: Low to High",
        orderBy: "price",
        sortDirection: "asc",
    },
    {
        value: "price-high",
        label: "Price: High to Low",
        orderBy: "price",
        sortDirection: "desc",
    },
];

const sort = computed({
    get() {
        const current = page.props.sortOption;

        return (
            SORT_OPTIONS.find(
                (option) =>
                    option.orderBy === current?.orderBy &&
                    option.sortDirection === current?.sortDirection,
            )?.value ?? SORT_OPTIONS[0].value
        );
    },
    set(value) {
        const option = SORT_OPTIONS.find((o) => o.value === value);

        router.reload({
            data: {
                orderBy: option.orderBy,
                sortDirection: option.sortDirection,
                page: 1,
            },
            only: ["items", "parsing", "sortOption"],
            preserveState: true,
        });
    },
});

const showSort = computed(
    () => !isConfirmedView.value && totalGifts.value >= 2,
);

function goToPage(n) {
    router.reload({
        data: { page: n },
        only: ["items", "parsing", "sortOption"],
        preserveState: true,
    });
}

// Live parsing poll: refresh until every new gift has been parsed, plus a
// bounded grace window for the late-arriving Cloudinary photo (GH-1018).
useGracePoll(() => page.props.parsing, {
    only: ["items", "parsing", "sortOption"],
});

// Reorder/confirmed routes render this page and open their modalog on top.
onMounted(() => {
    switch (route().current()) {
        case "list.reorder":
            modalogStore.showModalog({
                type: "ModalogWishListItemsReorder",
                props: {
                    itemsPerPage: 15,
                    listUuid: giftlist.value?.uuid || null,
                },
            });
            break;
        case "list.confirmed":
            if (!wishlistStore.isSpoiled) {
                modalogStore.showModalog({
                    type: "ModalogConfirmedGiftsSpoiler",
                    props: { list: giftlist.value },
                });
            }
            break;
        default:
            wishlistStore.isSpoiled = false;
            modalogStore.closeModalog();
            break;
    }
});
</script>

<template>
    <div>
        <Head :title="giftlist.name">
            <meta name="description" :content="pageDescription" />
            <meta
                property="og:title"
                :content="`${ownerName}'s “${giftlist.name}” Wish List`"
            />
            <meta
                property="og:description"
                :content="`Give ${ownerName} the perfect gift from their “${giftlist.name}” wish list`"
            />
            <meta property="og:url" :content="openGraph?.url" />
            <meta property="og:image" :content="openGraph?.share_image" />
            <link rel="canonical" :href="openGraph?.url" />
            <meta v-if="noIndex" name="robots" content="noindex, follow" />
        </Head>

        <!-- The gift panel ends 44px above the card bottom with rounded
             corners, per Figma 224-2044 (BH-59) -->
        <WishListDetailHero
            :owner="owner"
            :theme="owner.profile_theme"
            :list="giftlist"
            :total-gifts="totalGifts"
            round-bottom
            class="lg:pb-[44px]"
        >
            <template #mobile-band>
                <WishListMasthead
                    variant="mobile-banner"
                    :list="giftlist"
                    :is-owner="true"
                    :expert="isGiftHeroExpertList"
                />
            </template>

            <!-- Mobile: the white hero card wraps the masthead and the Add a
                 Gift CTA (GH-1079, like the My Lists hero); cream resumes at
                 the gifts heading below. -->
            <div class="max-lg:rounded-b-[10px] max-lg:bg-white lg:contents">
                <WishListMasthead
                    :list="giftlist"
                    :is-owner="true"
                    :expert="isGiftHeroExpertList"
                />
                <div v-if="!isConfirmedView" class="px-4 pb-5 pt-4 lg:hidden">
                    <AddGiftCard :list="giftlist" class="w-full" />
                </div>
            </div>

            <div class="px-4 pb-10 pt-2 lg:px-[44px] lg:pb-[44px]">
                <!-- Confirmed-gifts (spoiler) view -->
                <template v-if="isConfirmedView">
                    <template v-if="wishlistStore.isSpoiled">
                        <header
                            class="grid items-center gap-4 pt-4 max-lg:gap-5 max-lg:pt-10 lg:grid-cols-[1fr_auto_1fr]"
                        >
                            <ConfirmedGiftsToggle
                                variant="hide"
                                :list="giftlist"
                                class="max-lg:hidden lg:justify-self-start"
                            />
                            <h2
                                class="text-center font-brand text-[25px] font-bold text-[#102a3d]"
                            >
                                <strong>{{
                                    $page.props.totalConfirmed
                                }}</strong>
                                Confirmed Gifts
                            </h2>
                            <span class="max-lg:hidden"></span>
                        </header>

                        <div
                            v-if="items.length"
                            class="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                        >
                            <GiftCard
                                v-for="item in items"
                                :key="item.uuid"
                                :item="item"
                                :list="giftlist"
                                :spoiled="true"
                            />
                        </div>
                        <p
                            v-else
                            class="mt-10 text-center font-brand text-[16px] text-[#565a77]"
                        >
                            No gifts have been confirmed for this wish list yet.
                            Why not share your wish list?
                        </p>

                        <!-- Mobile: hide button follows the grid -->
                        <ConfirmedGiftsToggle
                            variant="hide"
                            :list="giftlist"
                            class="mt-8 w-full lg:hidden"
                        />
                    </template>
                </template>

                <!-- Standard list view -->
                <template v-else>
                    <header
                        class="grid items-center gap-4 pt-4 max-lg:gap-5 max-lg:pt-10 lg:grid-cols-[1fr_auto_1fr]"
                    >
                        <ConfirmedGiftsToggle
                            v-if="showConfirmedButton"
                            :list="giftlist"
                            class="max-lg:hidden lg:justify-self-start"
                        />
                        <span v-else class="max-lg:hidden"></span>
                        <h2
                            class="text-center font-brand text-[25px] font-bold text-[#102a3d]"
                        >
                            {{ giftsHeading }}
                        </h2>
                        <SortDropdown
                            v-if="showSort"
                            v-model="sort"
                            :options="SORT_OPTIONS"
                            class="lg:justify-self-end"
                        />
                    </header>

                    <div
                        class="mt-6 grid gap-6 max-lg:gap-[22px] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                    >
                        <AddGiftCard :list="giftlist" class="max-lg:hidden" />
                        <OnboardingCard v-if="totalGifts < 3" />
                        <GiftCard
                            v-for="item in items"
                            :key="item.uuid"
                            :item="item"
                            :list="giftlist"
                        />
                    </div>

                    <ListsPagination
                        v-if="meta?.last_page > 1"
                        :page="meta.current_page"
                        :pages="meta.last_page"
                        class="mt-10"
                        @update:page="goToPage"
                    />

                    <!-- Mobile: confirmed-gifts button follows the grid -->
                    <ConfirmedGiftsToggle
                        v-if="showConfirmedButton"
                        :list="giftlist"
                        class="mt-8 w-full lg:hidden"
                    />
                </template>
            </div>
        </WishListDetailHero>
    </div>
</template>
